/**
 * RenameUserModal — "Change username", the one dialog for it: an admin from
 * Admin → Users / Organization (a person's row), and the person themself from
 * Settings (the "Change username" row).
 *
 * The server decides who may (the person; a tenant or group admin for the
 * people in the tenants they manage; a super admin for anyone; nobody else
 * changes a super admin's) and re-checks the name with the one rule; the field
 * mirrors that rule as the name is typed (utils/personName). Every refused name
 * reads the same "Invalid username" (Segev, 2026-11-04) — whatever the reason.
 */

import { useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { groupAdminService } from '@modules/group_admin/services/groupAdminService';
import { PERSON_NAME_ERRORS, PERSON_NAME_MAX_LENGTH, checkPersonName, personNameErrorCode } from '@/utils/personName';
import './RenameTenantModal.css';
import './RenameUserModal.css';

interface RenameUserModalProps {
  /** The person's tenant and id (the admin route); unused with `save`. */
  tenantId?: string;
  userId?: string;
  currentName: string;
  email: string;
  onClose: () => void;
  /** Fired after the change is saved, with the stored name. */
  onRenamed: (newName: string) => void;
  /** How to save (the person themself saves through their own profile);
   *  default: the admin's user edit. */
  save?: (name: string) => Promise<void>;
}

/** The one message for a refused username; the others for who-may / gone / failed. */
export function useUsernameErrorText() {
  const { t } = useLanguage();
  return (code: string) => {
    if (PERSON_NAME_ERRORS.has(code)) return t('username.errors.invalid');
    if (code === 'ACCESS_DENIED' || code === 'NOT_FOUND') return t(`username.errors.${code}`);
    return t('username.errors.FAILED');
  };
}

export default function RenameUserModal({ tenantId, userId, currentName, email, onClose, onRenamed, save }: RenameUserModalProps) {
  const { t } = useLanguage();
  const errorText = useUsernameErrorText();
  const [value, setValue] = useState(currentName);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');

  const check = checkPersonName(value);
  const unchanged = check.name === currentName;
  const shownError = serverError || (touched && check.error ? errorText(check.error) : '');

  const submit = async () => {
    setTouched(true);
    if (check.error || unchanged || saving) return;
    setSaving(true);
    setServerError('');
    try {
      if (save) await save(check.name);
      else await groupAdminService.updateUser(tenantId!, userId!, { display_name: check.name });
      onRenamed(check.name);
    } catch (err) {
      setServerError(errorText(personNameErrorCode(err)));
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div className="modal rename-tenant-modal" role="dialog" aria-modal="true"
           aria-labelledby="rename-user-title" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title" id="rename-user-title">{t('username.change')}</h3>
          <button className="close-btn" onClick={onClose} disabled={saving} aria-label={t('common.close')}>×</button>
        </div>
        <div className="modal-content">
          <div className="form-group">
            <span className="rename-tenant-label">{t('username.currentName')}</span>
            <div className="rename-tenant-current">
              <bdi dir="auto">{currentName || email}</bdi>
              {currentName && <span className="rename-user-hint"> · <bdi dir="ltr">{email}</bdi></span>}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="rename-user-new">{t('username.newName')}</label>
            <input
              id="rename-user-new"
              dir="auto"
              className="input"
              value={value}
              maxLength={PERSON_NAME_MAX_LENGTH + 20}
              autoFocus
              autoComplete="off"
              aria-invalid={!!shownError}
              onChange={e => { setValue(e.target.value); setTouched(true); setServerError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') submit(); }}
            />
            {shownError
              ? <p className="rename-tenant-field-error" role="alert">{shownError}</p>
              : <p className="rename-user-hint">{t('username.hint')}</p>}
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>{t('username.cancel')}</button>
          <button type="button" className="btn-primary" disabled={saving || !!check.error || unchanged} onClick={submit}>
            {saving ? t('username.saving') : t('username.change')}
          </button>
        </div>
      </div>
    </div>
  );
}
